import { Info } from 'lucide-react'
import { useId, useRef, useState, type ReactNode } from 'react'
import { cn } from '@/lib/cn'

/**
 * Small "ⓘ" icon that reveals helper text on hover or keyboard focus.
 *
 * Implementation note: this is intentionally CSS-only (no Radix Tooltip,
 * no floating-ui). Radix briefly renders the floating layer at its
 * default position before measuring, which produced a visible flash
 * elsewhere on the page even with stable trigger dimensions and an
 * app-level provider. A plain absolutely-positioned panel below the
 * icon doesn't depend on async measurement, so the panel always shows
 * up exactly where it should from the very first hover.
 *
 * Trade-off: no collision detection. Pass `placement="left"` if the
 * icon is near the right edge of its container; otherwise the default
 * "right" places the panel below-and-right of the icon, which works
 * well for modal titles, form labels, and toolbar headers.
 */
export interface HelpTooltipProps {
  text: ReactNode
  className?: string
  /** Inline-icon size in px. Defaults to 14. */
  size?: number
  /**
   * Where the panel sits relative to the trigger.
   *  - `right` (default) → below-right of the icon
   *  - `left`            → below-left of the icon (use near right edges)
   *  - `top-right`       → above-right of the icon
   */
  placement?: 'right' | 'left' | 'top-right'
  /** Tailwind width cap so multi-sentence copy wraps. */
  maxWidthClassName?: string
}

export function HelpTooltip({
  text,
  className,
  size = 14,
  placement = 'right',
  maxWidthClassName = 'max-w-xs',
}: HelpTooltipProps) {
  const id = useId()
  const [hovered, setHovered] = useState(false)
  // Why: Radix Dialog auto-focuses the first focusable element on open.
  // If the help icon ends up first, that focus would set open=true and
  // the tooltip would appear without the user touching it. Gate focus
  // events on "user has interacted with the modal" so the auto-focus
  // on mount is ignored, but real keyboard navigation (tabbing onto
  // the icon) still reveals the hint.
  const [keyboardFocused, setKeyboardFocused] = useState(false)
  const interactedRef = useRef(false)

  const panelPosition =
    placement === 'left'
      ? 'right-0 top-full mt-1.5'
      : placement === 'top-right'
        ? 'left-0 bottom-full mb-1.5'
        : 'left-0 top-full mt-1.5'

  const open = hovered || keyboardFocused

  return (
    <span
      className="relative inline-flex"
      // Once any pointer/key event lands inside the wrapper, treat
      // future focus events as user-driven. Captured at the wrapper so
      // a keydown elsewhere doesn't qualify.
      onMouseDown={() => { interactedRef.current = true }}
      onKeyDown={() => { interactedRef.current = true }}
    >
      <button
        type="button"
        aria-describedby={open ? id : undefined}
        aria-label="More info"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onFocus={() => {
          if (interactedRef.current) setKeyboardFocused(true)
        }}
        onBlur={() => setKeyboardFocused(false)}
        style={{ width: size + 4, height: size + 4 }}
        className={cn(
          'inline-flex shrink-0 items-center justify-center align-middle rounded-full text-muted-foreground transition-colors hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          className,
        )}
      >
        <Info style={{ width: size, height: size }} aria-hidden />
      </button>
      {open ? (
        <span
          id={id}
          role="tooltip"
          // pointer-events-none so the panel doesn't steal the trigger's
          // mouseleave when the user moves the cursor across it. z-50 to
          // float above modal headers. text-muted-foreground keeps the
          // helper copy visually subordinate to the heading next to it.
          className={cn(
            'pointer-events-none absolute z-50 whitespace-normal rounded-sm border bg-card px-2 py-1 text-xs font-normal leading-snug text-muted-foreground shadow-card',
            panelPosition,
            maxWidthClassName,
          )}
          style={{ width: 'max-content', maxWidth: '20rem' }}
        >
          {text}
        </span>
      ) : null}
    </span>
  )
}
